import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { apiRequest } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorHandler';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import CountyFilterSelect, { countyFilterToId, regionFilterToId } from '../../components/reports/CountyFilterSelect';
import { 
  FileText, 
  Calendar,
  Search,
  RefreshCw,
  AlertTriangle,
  Clock,
  User,
  Ticket as TicketIcon,
  ChevronLeft,
  ChevronRight,
  Download,
  ChevronDown,
  Filter
} from 'lucide-react';
import { formatDateTime } from '../../components/globaldeclaration';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../utils/exportStyledXlsx';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const getCurrentMonthDateRange = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const formatLocalDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return {
    fromDate: formatLocalDate(firstOfMonth),
    toDate: formatLocalDate(today)
  };
};

const SlaBreachedReport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(() => getCurrentMonthDateRange().fromDate);
  const [toDate, setToDate] = useState(() => getCurrentMonthDateRange().toDate);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [chartStats, setChartStats] = useState({ totalEscalated: 0, byStatus: [], byCategory: [] });
  const [chartDataLoading, setChartDataLoading] = useState(false);
  const listFetchIdRef = useRef(0);
  const statsFetchIdRef = useRef(0);
  const skipNextPageFetchRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const extractPagePayload = useCallback((response) => {
    const root = response?.data?.data ?? response?.data ?? response;
    if (root && Array.isArray(root.content)) return root;
    if (Array.isArray(root)) {
      return {
        content: root,
        totalElements: root.length,
        totalPages: 1,
        pageNumber: 0,
        pageSize: root.length || pageSize,
        first: true,
        last: true
      };
    }
    return null;
  }, [pageSize]);

  const loadStatistics = useCallback(async (rangeFrom = fromDate, rangeTo = toDate) => {
    if (!rangeFrom || !rangeTo) return;
    const fetchId = ++statsFetchIdRef.current;
    try {
      setChartDataLoading(true);
      const body = { fromDate: rangeFrom, toDate: rangeTo };
      const regionId = regionFilterToId(selectedRegion);
      if (regionId !== null) body.regionId = regionId;
      const countyId = countyFilterToId(selectedCounty);
      if (countyId !== null) body.countyId = countyId;
      const response = await apiRequest('/gateway/api/ticket/reports/sla-breached/statistics', {
        method: 'POST',
        body
      });
      if (fetchId !== statsFetchIdRef.current) return;
      const data = response?.data?.data ?? response?.data ?? {};
      setChartStats({
        totalEscalated: Number(data.totalEscalated) || 0,
        byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
        byCategory: Array.isArray(data.byCategory) ? data.byCategory : []
      });
    } catch (err) {
      if (fetchId !== statsFetchIdRef.current) return;
      console.warn('SLA breached statistics load failed:', err);
      setChartStats({ totalEscalated: 0, byStatus: [], byCategory: [] });
    } finally {
      if (fetchId === statsFetchIdRef.current) {
        setChartDataLoading(false);
      }
    }
  }, [fromDate, toDate, selectedRegion, selectedCounty]);

  const loadList = useCallback(async (options = {}) => {
    const rangeFrom = options.fromDate ?? fromDate;
    const rangeTo = options.toDate ?? toDate;
    const page = options.page ?? currentPage;
    const search = options.search !== undefined ? options.search : debouncedSearchTerm;

    if (!rangeFrom || !rangeTo) return;
    if (new Date(rangeFrom) > new Date(rangeTo)) {
      toast.error('From Date must be before or equal to To Date');
      return;
    }

    const fetchId = ++listFetchIdRef.current;
    try {
      setLoading(true);
      setError('');
      const body = {
        fromDate: rangeFrom,
        toDate: rangeTo,
        page,
        size: pageSize
      };
      if (search) body.search = search;
      const regionId = regionFilterToId(selectedRegion);
      if (regionId !== null) body.regionId = regionId;
      const countyId = countyFilterToId(selectedCounty);
      if (countyId !== null) body.countyId = countyId;

      const response = await apiRequest('/gateway/api/ticket/reports/sla-breached', {
        method: 'POST',
        body
      });
      if (fetchId !== listFetchIdRef.current) return;

      const pagePayload = extractPagePayload(response);
      const content = Array.isArray(pagePayload?.content) ? pagePayload.content : [];
      setTickets(content);
      setPaginationData({
        totalElements: pagePayload?.totalElements ?? content.length,
        totalPages: pagePayload?.totalPages ?? Math.max(1, Math.ceil((pagePayload?.totalElements ?? content.length) / pageSize) || 1),
        pageNumber: pagePayload?.pageNumber ?? page,
        pageSize: pagePayload?.pageSize ?? pageSize,
        first: pagePayload?.first ?? page === 0,
        last: pagePayload?.last ?? true
      });
    } catch (err) {
      if (fetchId !== listFetchIdRef.current) return;
      console.error('Error loading SLA breached list:', err);
      setError(extractErrorMessage(err, 'Failed to load report'));
      toast.error(extractErrorMessage(err, 'Failed to load report'));
      setTickets([]);
      setPaginationData(null);
    } finally {
      if (fetchId === listFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [fromDate, toDate, currentPage, pageSize, debouncedSearchTerm, selectedRegion, selectedCounty, extractPagePayload]);

  const refreshBoth = useCallback(async (options = {}) => {
    const resetToFirstPage = options.resetToFirstPage !== false;
    const rangeFrom = options.fromDate ?? fromDate;
    const rangeTo = options.toDate ?? toDate;
    if (!rangeFrom || !rangeTo) return;
    if (new Date(rangeFrom) > new Date(rangeTo)) {
      toast.error('From Date must be before or equal to To Date');
      return;
    }

    const pageToUse = resetToFirstPage ? 0 : currentPage;
    if (resetToFirstPage && currentPage !== 0) {
      skipNextPageFetchRef.current = true;
      setCurrentPage(0);
    }

    await Promise.all([
      loadStatistics(rangeFrom, rangeTo),
      loadList({
        fromDate: rangeFrom,
        toDate: rangeTo,
        page: pageToUse,
        search: options.search !== undefined ? options.search : debouncedSearchTerm
      })
    ]);
  }, [fromDate, toDate, currentPage, debouncedSearchTerm, loadStatistics, loadList]);

  // Initial load: statistics + list page 0
  useEffect(() => {
    if (!hasGeneratedReport || !fromDate || !toDate) return;
    (async () => {
      await refreshBoth({ resetToFirstPage: true, fromDate, toDate, search: '' });
      initialLoadDoneRef.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination / page size → list only
  useEffect(() => {
    if (!hasGeneratedReport || !initialLoadDoneRef.current) return;
    if (skipNextPageFetchRef.current) {
      skipNextPageFetchRef.current = false;
      return;
    }
    loadList({ page: currentPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Search → list only (reset to page 0)
  useEffect(() => {
    if (!hasGeneratedReport || !initialLoadDoneRef.current) return;
    if (currentPage !== 0) {
      skipNextPageFetchRef.current = true;
      setCurrentPage(0);
    }
    loadList({ page: 0, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const generateReport = async () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both From Date and To Date');
      return;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      toast.error('From Date must be before or equal to To Date');
      return;
    }
    setHasGeneratedReport(true);
    await refreshBoth({ resetToFirstPage: true });
    toast.success('Report generated');
  };

  const fetchAllSlaBreachedForExport = async () => {
    const body = { fromDate, toDate, page: 0, size: 10 };
    if (debouncedSearchTerm) body.search = debouncedSearchTerm;
    const regionId = regionFilterToId(selectedRegion);
    if (regionId !== null) body.regionId = regionId;
    const countyId = countyFilterToId(selectedCounty);
    if (countyId !== null) body.countyId = countyId;
    const first = await apiRequest('/gateway/api/ticket/reports/sla-breached', {
      method: 'POST',
      body
    });
    const firstPage = extractPagePayload(first);
    const total = Number(firstPage?.totalElements) || 0;
    if (total <= 0) return [];
    if (total <= 10 && Array.isArray(firstPage?.content)) return firstPage.content;
    const fullBody = { fromDate, toDate, page: 0, size: total };
    if (debouncedSearchTerm) fullBody.search = debouncedSearchTerm;
    if (regionId !== null) fullBody.regionId = regionId;
    if (countyId !== null) fullBody.countyId = countyId;
    const full = await apiRequest('/gateway/api/ticket/reports/sla-breached', {
      method: 'POST',
      body: fullBody
    });
    const fullPage = extractPagePayload(full);
    return Array.isArray(fullPage?.content) ? fullPage.content : [];
  };

  const pieChartData = useMemo(() => {
    return (chartStats.byStatus || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        value: Number(item.count) || 0
      }))
      .filter((d) => d.value > 0);
  }, [chartStats]);

  const barChartData = useMemo(() => {
    return (chartStats.byCategory || [])
      .map((item) => {
        const fullName = String(item.name || 'Uncategorized').trim() || 'Uncategorized';
        return {
          name: fullName.substring(0, 20),
          count: Number(item.count) || 0,
          fullName
        };
      })
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [chartStats]);

  const CHART_COLORS = ['#EF4444', '#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899'];
  const hasChartData = pieChartData.length > 0 || barChartData.length > 0;

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    if (!fromDate || !toDate) {
      toast.error('Please select date range and generate report first');
      return;
    }
    setExporting(true);
    try {
      const allTickets = await fetchAllSlaBreachedForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = [
        'S.NO',
        'Ticket #',
        'Subject',
        'Requester',
        'Category',
        'Status',
        'Escalated Time',
        'Assigned Time',
        'SLA Breach',
      ];
      const rows = allTickets.map((t, i) => [
        i + 1,
        t.ticketNumber || 'N/A',
        t.subject || 'N/A',
        t.requesterEmail || 'N/A',
        t.category || 'N/A',
        t.status || 'N/A',
        // Raw timestamps for dateColumns; invalid/empty stay text via exportStyledXlsx
        t.escalatedTime || '',
        t.assignedTime || '',
        t.slaBreach ? 'Breached' : '-',
      ]);
      downloadStyledXlsx({
        fileName: `sla-breached-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'SLA Breached Report',
        headers,
        rows,
        numberColumns: [0],
        textColumns: [1, 2, 3, 4, 5, 8],
        dateColumns: [6, 7],
        centerColumns: [0, 5, 6, 7, 8],
      });
      toast.success('Report exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    if (!fromDate || !toDate) {
      toast.error('Please select date range and generate report first');
      return;
    }
    setExporting(true);
    try {
      const allTickets = await fetchAllSlaBreachedForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('SLA Breached Report', 14, 15);
      doc.setFontSize(10);
      const tableData = allTickets.map((t, i) => [
        i + 1,
        t.ticketNumber || 'N/A',
        (t.subject || 'N/A').substring(0, 40),
        (t.requesterEmail || 'N/A').substring(0, 25),
        t.category || 'N/A',
        t.status || 'N/A',
        formatDateTime(t.escalatedTime),
        t.assignedTime ? formatDateTime(t.assignedTime) : '',
        t.slaBreach ? 'Breached' : '-'
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'Ticket #', 'Subject', 'Requester', 'Category', 'Status', 'Escalated Time', 'Assigned Time', 'SLA Breach']],
        body: tableData,
        styles: { fontSize: 7 }
      });
      doc.save(`sla-breached-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (paginationData && newPage >= 0 && newPage < paginationData.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">SLA Breached Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View tickets that were escalated within a specified date range
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowFilters((prev) => !prev);
              setExportMenuOpen(false);
            }}
            className="flex items-center gap-2"
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
            Filter
            {hasGeneratedReport && !showFilters && (
              <span className="ml-1 h-2 w-2 rounded-full bg-primary" aria-hidden />
            )}
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting || !fromDate || !toDate || !paginationData?.totalElements}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportExcel}>
                  <FileText className="h-4 w-4" /> Export Excel
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              refreshBoth({ resetToFirstPage: false });
            }}
            disabled={loading || chartDataLoading || !hasGeneratedReport || !fromDate || !toDate}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filter Card (toggle via Filter button) */}
      {showFilters && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
           Date Range Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                From Date
              </label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                To Date
              </label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="md:col-span-1">
              <CountyFilterSelect
                regionValue={selectedRegion}
                countyValue={selectedCounty}
                onRegionChange={setSelectedRegion}
                onCountyChange={setSelectedCounty}
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button
              onClick={generateReport}
              disabled={loading || !fromDate || !toDate}
              className="w-auto"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Charts: Pie (by status) and Bar (by category) — from statistics API */}
      {!chartDataLoading && hasChartData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TicketIcon className="h-5 w-5" />
                SLA Breached by status
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Distribution of all tickets in the selected date range
              </p>
            </CardHeader>
            <CardContent>
              {pieChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieChartData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [value, 'Tickets']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-5 w-5" />
                SLA Breached by category
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Count of all tickets per category in the selected date range
              </p>
            </CardHeader>
            <CardContent>
              {barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" allowDecimals={false} className="text-xs" />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value) => [value, 'Tickets']}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill={CHART_COLORS[0]} name="Tickets" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results Table */}
      {(tickets.length > 0 || loading || (paginationData && paginationData.totalElements > 0) || debouncedSearchTerm) && hasGeneratedReport && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TicketIcon className="h-5 w-5" />
                Escalated Tickets ({paginationData ? paginationData.totalElements : tickets.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse min-w-[900px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[4%]">S.NO</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[10%]">Ticket #</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[28%]">Subject</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[14%]">Requester</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[10%]">Category</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[8%]">Status</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[12%]">Escalated Time</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[12%]">Assigned Time</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[8%]">SLA Breach</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center p-8 text-muted-foreground">
                        {loading ? 'Loading tickets...' : (searchTerm ? 'No tickets found matching your search criteria' : 'No tickets found')}
                      </td>
                    </tr>
                  ) : (
                    tickets.map((ticket, index) => {
                      const serialNumber = (currentPage * pageSize) + index + 1;
                      return (
                      <tr key={ticket.ticketId || ticket.ticketNumber} className="border-b border-border hover:bg-muted/50">
                        <td className="p-3 text-sm text-foreground">
                          {serialNumber}
                        </td>
                        <td className="p-3 text-sm">
                          <span className="font-medium text-foreground">
                            {ticket.ticketNumber || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <p
                            className="break-all line-clamp-3 leading-snug"
                            title={ticket.subject || 'N/A'}
                          >
                            {ticket.subject || 'N/A'}
                          </p>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <div className="flex items-center gap-2 min-w-0">
                            <User className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="truncate" title={ticket.requesterEmail || 'N/A'}>
                              {ticket.requesterEmail || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {ticket.category || 'N/A'}
                        </td>
                        <td className="p-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            ticket.status === 'escalated' 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}>
                            {ticket.status || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {formatDateTime(ticket.escalatedTime)}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {ticket.assignedTime ? formatDateTime(ticket.assignedTime) : ''}
                        </td>
                        <td className="p-3 text-sm">
                          {ticket.slaBreach ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Breached
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {paginationData && paginationData.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing <span className="font-medium">{(paginationData.pageNumber * paginationData.pageSize) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min((paginationData.pageNumber + 1) * paginationData.pageSize, paginationData.totalElements)}</span> of{' '}
                  <span className="font-medium">{paginationData.totalElements}</span> tickets
                  {' '}(Page {paginationData.pageNumber + 1} of {paginationData.totalPages})
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={paginationData.first || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(paginationData.totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (paginationData.totalPages <= 5) {
                        pageNum = i;
                      } else if (currentPage < 3) {
                        pageNum = i;
                      } else if (currentPage > paginationData.totalPages - 4) {
                        pageNum = paginationData.totalPages - 5 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className="min-w-[2.5rem]"
                        >
                          {pageNum + 1}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={paginationData.last || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !chartDataLoading && tickets.length === 0 && !hasChartData && fromDate && toDate && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Escalated Tickets Found</h3>
            <p className="text-sm text-muted-foreground">
              No tickets were escalated within the selected date range.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SlaBreachedReport;

